import axios from 'axios';
import { secureLocalStorage } from '../../utils/crypto';
import { setupInterceptors } from '../core/axiosInterceptor.js';
import { getTenantId, initializeTenant, getTenantConfig, loadTenantConfig } from '../tenant/tenantService.js';
import { jwtDecode } from 'jwt-decode';

const PARTICIPANTS_URL = process.env.REACT_APP_PARTICIPANTS_URL;
const WALLET_URL = process.env.REACT_APP_WALLET_URL;
const MARKETPLACE_URL = process.env.REACT_APP_MARKETPLACE_URL;
const ENGAGEMENT_URL = process.env.REACT_APP_ENGAGEMENT_URL;

const AUTO_REFRESH_TOKEN = process.env.REACT_APP_AUTO_REFRESH_TOKEN === 'true';
const REFRESH_TOKEN_BEFORE_EXPIRY = parseInt(process.env.REACT_APP_REFRESH_TOKEN_BEFORE_EXPIRY || '300000', 10);

// Create axios instances
export const api = axios.create({
  baseURL: PARTICIPANTS_URL,
  timeout: 20000,
});

const apiWallet = axios.create({
  baseURL: WALLET_URL,
  timeout: 20000,
});

export const apiMarketplace = axios.create({
  baseURL: MARKETPLACE_URL,
  timeout: 20000,
});

const apiEngagement = axios.create({
  baseURL: ENGAGEMENT_URL,
  timeout: 20000,
});

// Ensure X-Tenant-Id is always present in requests
const ensureTenantHeader = async (config) => {
  let tenantId = getTenantId();
  
  // If tenant ID is not available, initialize it
  if (!tenantId) {
    try {
      await initializeTenant();
      tenantId = getTenantId();
    } catch (error) {
      console.error('Failed to initialize tenant during API call:', error);
    }
  }
  
  if (tenantId) {
    config.headers = config.headers || {};
    config.headers['X-Tenant-Id'] = tenantId;
    config.headers['Content-Type'] = 'application/json';
  }
  
  return config;
};

// Add request interceptor to ensure tenant ID is included
api.interceptors.request.use(ensureTenantHeader);
apiWallet.interceptors.request.use(ensureTenantHeader);
apiEngagement.interceptors.request.use(ensureTenantHeader);

// Setup other interceptors
setupInterceptors(api);
setupInterceptors(apiWallet);
setupInterceptors(apiEngagement);

const processLoginResponse = (response) => {
  if (response.data) {
    const idToken = response.data.idToken;
    const accessToken = response.data.accessToken;
    const refreshToken = response.data.refreshToken;
    const expiresIn = response.data.expiresIn || 3600;
    
    if (idToken && accessToken) {
      const expiresAt = new Date().getTime() + expiresIn * 1000;

      secureLocalStorage.setItem('idToken', idToken);
      secureLocalStorage.setItem('accessToken', accessToken);
      secureLocalStorage.setItem('tokenExpiresAt', expiresAt.toString());

      if (refreshToken) {
        secureLocalStorage.setItem('refreshToken', refreshToken);
      }

      // Limpa cache do segment ao fazer login para forçar busca novamente
      localStorage.removeItem('userSegments');

      // Set authorization header for all API instances
      api.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
      apiWallet.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
      apiMarketplace.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
      apiEngagement.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;

      response.data.token = idToken;

      if (AUTO_REFRESH_TOKEN && refreshToken) {
        setupRefreshTokenTimer(expiresIn, refreshToken);
      }
    } else if (idToken || accessToken) {
      // Resposta parcial: um token presente sem o outro — estado inconsistente, não salva nada
      throw new Error('Resposta de login incompleta: par de tokens ausente ou corrompido');
    }
  }

  return response.data;
};

let refreshTokenTimeout;
const setupRefreshTokenTimer = (expiresIn, refreshToken) => {
  if (refreshTokenTimeout) {
    clearTimeout(refreshTokenTimeout);
  }
  
  const refreshTime = (expiresIn * 1000) - REFRESH_TOKEN_BEFORE_EXPIRY;
  
  if (refreshTime > 0) {
    refreshTokenTimeout = setTimeout(async () => {
      try {
        const username = secureLocalStorage.getItem('username');
        if (username && refreshToken) {
          await userService.refreshAccessToken(refreshToken, username);
        }
      } catch (error) {
        console.error('Erro ao renovar token automaticamente:', error);
      }
    }, refreshTime);
  }
};

// Initialize authorization headers for all API instances
const initializeAuthHeaders = () => {
  const idToken = secureLocalStorage.getItem('idToken');
  if (idToken) {
    api.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
    apiWallet.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
    apiMarketplace.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
  }
};

const normalizeUserSegment = (segment) => {
  const segmentId = segment?.SegmentId || segment?.segmentId || segment?.id;
  return segmentId ? { ...segment, segmentId } : null;
};

// Initialize headers when module loads
initializeAuthHeaders();

/**
 * Extrai o profile do JWT token
 * Tenta primeiro 'profile', depois 'custom:profile'
 * Retorna 'default' se não encontrar
 * @returns {string} Profile do usuário ou 'default'
 */
export const getUserProfile = () => {
  try {
    const idToken = secureLocalStorage.getItem('idToken');
    if (idToken) {
      const decodedToken = jwtDecode(idToken);
      return decodedToken.profile || decodedToken['custom:profile'] || 'default';
    }
  } catch (error) {
    console.error('Error decoding token for profile:', error);
  }
  return 'default';
};

export const userService = {
  async login(credentials) {
    try {
      // Ensure tenant is initialized before login
      await initializeTenant();
      
      const response = await api.post('/api/Participants/login', credentials);
      const loginResult = processLoginResponse(response);
      
      // Após login bem-sucedido, busca o segment do usuário e dispara evento
      if (loginResult && this.isAuthenticated()) {
        // Aguarda um pequeno delay para garantir que os tokens foram salvos
        setTimeout(async () => {
          try {
            const segments = await this.getUserSegments(true);
            if (segments.length > 0) {
              window.dispatchEvent(new CustomEvent('userSegmentLoaded', {
                detail: { segmentIds: segments.map(s => s.segmentId) }
              }));
            }
            // Dispara evento de login para que outros componentes possam reagir
            console.info('[push] userService: disparando evento userLoggedIn');
            window.dispatchEvent(new CustomEvent('userLoggedIn'));
          } catch (error) {
            console.warn('Failed to fetch user segment after login:', error);
            // Dispara evento mesmo em caso de erro para que o tema possa recarregar com SEGMENT_DEFAULT
            console.info('[push] userService: disparando evento userLoggedIn (após erro no segment)');
            window.dispatchEvent(new CustomEvent('userLoggedIn'));
          }
        }, 100);
      }
      
      return loginResult;
    } catch (error) {
      throw error;
    }
  },

  async refreshAccessToken(refreshToken, userName) {
    try {
      const storedRefreshToken = refreshToken || secureLocalStorage.getItem('refreshToken');
      const storedUserName = userName || secureLocalStorage.getItem('username');
      
      if (!storedRefreshToken || !storedUserName) {
        throw new Error('Refresh token ou username não encontrados');
      }
      
      const response = await api.post('/api/Participants/refresh-token', {
        refreshToken: storedRefreshToken,
        userName: storedUserName
      });
      
      return processLoginResponse(response);
    } catch (error) {
      console.error('Refresh token error:', error.response?.data || error.message);
      this.logout();
      throw new Error('Failed to refresh token');
    }
  },

  async getUserInfo(token) {
    try {
      await this.checkTokenExpiration();
      
      const authToken = token || secureLocalStorage.getItem('idToken');
      
      if (!authToken) {
        throw new Error('No authentication token found');
      }
      
      const response = await api.get('/api/Participants/info', {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('Get user info error:', error.response?.data || error.message);
      throw new Error('Failed to fetch user info');
    }
  },

  async updateUser(token, userData) {
    try {
      await this.checkTokenExpiration();
      
      const authToken = token || secureLocalStorage.getItem('idToken');
      
      if (!authToken) {
        throw new Error('No authentication token found');
      }
      
      const response = await api.put('/api/Participants', userData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('Update user error:', error.response?.data || error.message);
      // Mantém a resposta da API para as telas lerem os códigos de erro (ex.: Participant.PhoneAlreadyInUse)
      const updateError = new Error('Failed to update user');
      updateError.response = error.response;
      throw updateError;
    }
  },

  async registerUser(registerData) {

    const getCurrentLocation = () =>
      new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          return resolve({ latitude: '', longitude: '' });
        }
    
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude.toString(),
              longitude: position.coords.longitude.toString()
            });
          },
          () => {
            resolve({ latitude: '', longitude: '' });
          }
        );
      });

    try {
      await initializeTenant();
      
      let formattedData = registerData;
      

      if (!registerData.login || !registerData.participant) {
        const registerDataFormatted = registerData;
        const location = await getCurrentLocation();
        formattedData = {
          cognitoPassword: {
            password: registerDataFormatted.cognitoPassword.password || ''
          },
          participant: {
            profile: registerDataFormatted.participant.profile || 'gold',
            name: registerDataFormatted.participant.name || '',
            tenantId: getTenantId(),
            document: registerDataFormatted.participant.document || '',
            userName: registerDataFormatted.participant.userName || '',
            documentType: registerDataFormatted.participant.documentType || 0,
            ...(registerDataFormatted.participant.document.length === 11 && { birthDate: registerDataFormatted.participant.birthDate }),
            status: registerDataFormatted.participant.status || 0,
            emails: registerDataFormatted.participant.emails || [
              {
                emailAddress: registerDataFormatted.participant.email || '',
                isValidated: false,
                isPrimary: true
              }
            ],
            addresses: registerDataFormatted.participant.addresses || [],
            phones: registerDataFormatted.participant.phones || [],
            attributes: registerDataFormatted.participant.attributes || {},
            identityId: registerDataFormatted.participant.document
          },
          terms: {
            assetIds: [...(registerDataFormatted.participant.terms?.assetIds || [])],
            info: {
              fingerPrint: registerDataFormatted.participant.document,
              browser: registerDataFormatted.participant.attributes.document_tracking_browser,
              geoLocation: {
                latitude: location.latitude,
                longitude: location.longitude
              }
            }
          },
          ...(registerDataFormatted.referralCode && {
            referralCode: registerDataFormatted.referralCode
          })
        };
      }
      
      const response = await api.post('/api/Participants', formattedData);
      return response.data;
    } catch (error) {
      console.error('Register user error:', error.response?.data || error.message);
      throw error;
    }
  },
  
  async checkTokenExpiration() {
    if (!AUTO_REFRESH_TOKEN) return;
    
    const tokenExpiresAt = secureLocalStorage.getItem('tokenExpiresAt');
    const refreshToken = secureLocalStorage.getItem('refreshToken');
    const username = secureLocalStorage.getItem('username');
    
    if (tokenExpiresAt && refreshToken && username) {
      const expiresAt = parseInt(tokenExpiresAt, 10);
      const now = new Date().getTime();
      
      if (expiresAt - now < REFRESH_TOKEN_BEFORE_EXPIRY) {
        const response = await this.refreshAccessToken(refreshToken, username);
        if (response) {
          secureLocalStorage.setItem('idToken', response.idToken);
          secureLocalStorage.setItem('accessToken', response.accessToken);
          
          // Update authorization headers for all API instances
          api.defaults.headers.common['Authorization'] = `Bearer ${response.idToken}`;
          apiWallet.defaults.headers.common['Authorization'] = `Bearer ${response.idToken}`;
          apiMarketplace.defaults.headers.common['Authorization'] = `Bearer ${response.idToken}`;
        }
      }
    }
  },
  
  isAuthenticated() {
    const token = secureLocalStorage.getItem('idToken');
    const tokenExpiresAt = secureLocalStorage.getItem('tokenExpiresAt');
    
    if (!token) return false;
    
    if (!tokenExpiresAt) return true;
    
    const expiresAt = parseInt(tokenExpiresAt, 10);
    const now = new Date().getTime();
    
    return expiresAt > now;
  },
  
  logout() {
    if (refreshTokenTimeout) {
      clearTimeout(refreshTokenTimeout);
    }
    
    secureLocalStorage.removeItem('idToken');
    secureLocalStorage.removeItem('accessToken');
    secureLocalStorage.removeItem('tokenExpiresAt');
    secureLocalStorage.removeItem('refreshToken');
    secureLocalStorage.removeItem('username');
    secureLocalStorage.removeItem('termsAccepted');
    
    // Limpa cache do segment do usuário ao deslogar
    localStorage.removeItem('userSegments');
    
    // Clear authorization headers from all API instances
    delete api.defaults.headers.common['Authorization'];
    delete apiWallet.defaults.headers.common['Authorization'];
    delete apiMarketplace.defaults.headers.common['Authorization'];
    delete apiEngagement.defaults.headers.common['Authorization'];
  },

  async requestPasswordReset(userName, turnstileToken) {
    try {
      // Ensure tenant is initialized before password reset
      await initializeTenant();

      const formattedUserName = userName;

      const payload = {
        userName: formattedUserName,
        turnstileToken: turnstileToken || undefined
      };

      const response = await api.post('/api/Participants/forgot-password', payload);
      
      return response.data;
    } catch (error) {
      // Preserve HTTP status codes used by the UI (429/403) instead of hiding them behind a generic Error.
      if (error.response?.status === 429 || error.response?.status === 403) {
        throw error;
      }
      const code = error.response?.data?.[0]?.code || 'requestOTPFailed';
      throw new Error(`errors.${code}`);
    }
  },

  async resetPassword(userName, otpCode, newPassword) {
    try {
      const formattedUserName = userName;
      
      const response = await api.post('/api/Participants/reset-password', {
        userName: formattedUserName,
        otpCode: otpCode,
        newPassword: newPassword
      });
      
      return response.data;
    } catch (error) {
      console.error('Reset password error:', error.response?.data || error.message);
      throw new Error('Failed to reset password');
    }
  },

  async resendConfirmationCode(data){
    try {
      // Preparar payload com forceSms (usa valor fornecido ou false como padrão)
      const payload = {
        ...data,
        forceSms: data.forceSms !== undefined ? data.forceSms : false
      };
      
      const response = await api.post('/api/Participants/resend-confirmation-code', payload);
      
      if (!response.data) {
        throw new Error('No response data received');
      }
      
      const maskedEmail = response.data?.codeDeliveryDetails?.destination || '';
      
      return {
        success: true,
        data: response.data,
        maskedEmail: maskedEmail
      };
    } catch (error) {
      console.error('Resend confirmation code error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to resend confirmation code');
    }
  },

  async checkUsername(username) {
    try {
      // Ensure tenant is initialized before checking username
      await initializeTenant();
      
      const response = await api.post(`/api/Participants/check-by-username`, {
        userName: username
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return false;
      }
      console.error('Check username error:', error.response?.data || error.message);
      throw new Error('Failed to check username');
    }
  },

  async checkByDocument(document) {
    try {
      // Ensure tenant is initialized before checking document
      await initializeTenant();
      
      const response = await api.post(`/api/participants/check-by-document`, {
        document: document
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return {
          exists: false,
          isPreRegister: false,
          isCreatedInCognito: false,
          isEmailValidated: false
        };
      }
      console.error('Check document error:', error.response?.data || error.message);
      throw new Error('Failed to check document');
    }
  },

  async checkByMobile(phone) {
    try {
      await initializeTenant();

      const response = await api.post(`/api/participants/check-by-mobile`, {
        phone
      });
      return response.data;
    } catch (error) {
      console.error('Check mobile error:', error.response?.data || error.message);
      // Diferente dos outros checks, o 400 valida o celular: mantém a resposta para ler os códigos
      const checkError = new Error('Failed to check mobile');
      checkError.response = error.response;
      throw checkError;
    }
  },

  async changePassword(currentPassword, newPassword) {
    try {
      await this.checkTokenExpiration();
      
      const accessToken = secureLocalStorage.getItem('accessToken');
      
      if (!accessToken) {
        throw new Error('No access token found');
      }
      
      const response = await api.post('/api/Participants/change-password', {
        currentPassword,
        newPassword
      }, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('Change password error:', error.response?.data || error.message);
      throw new Error('Failed to change password');
    }
  },

  async getWalletAccounts() {
    try {
      // Buscar carteiras diretamente da configuração do tenant (campo WALLETS)
      const config = await loadTenantConfig();
      const wallets = config?.WALLETS;

      // Garantir que sempre retornamos um array
      return Array.isArray(wallets) ? wallets : [];
    } catch (error) {
      console.error('Get wallet accounts error (from tenant config):', error.message);
      // Retornar array vazio em caso de erro ao invés de lançar exceção
      return [];
    }
  },

  async getBalance(accountingLedgerId) {
    try {
      await this.checkTokenExpiration();
      
      const authToken = secureLocalStorage.getItem('idToken');
      
      if (!authToken) {
        throw new Error('No authentication token found');
      }
      if (!accountingLedgerId) {
        throw new Error('No accounting ledger id found');
      }
      
      const response = await api.get(`/api/Wallet/balance?accountingLedgerId=${accountingLedgerId}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('Get balance error:', error.response?.data || error.message);
      throw new Error('Failed to fetch balance');
    }
  },

  confirmSignup: async ({ userName, confirmationCode }) => {
    try {
      const response = await api.post('/api/Participants/confirm-signup', {
        userName,
        confirmationCode
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async getStatement(startDate, endDate, accountingLedgerId) {
    try {
      await this.checkTokenExpiration();
      
      const authToken = secureLocalStorage.getItem('idToken');
      
      if (!authToken) {
        throw new Error('No authentication token found');
      }
      
      const params = {
        startDate,
        endDate
      };
      
      if (accountingLedgerId) {
        params.accountingLedgerId = accountingLedgerId;
      }
      
      const response = await api.get('/api/Wallet/statement', {
        params,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('Get statement error:', error.response?.data || error.message);
      throw new Error('Failed to fetch statement');
    }
  },

  async testUnauthorized() {
    try {
      // Força um token inválido para simular erro 401
      const invalidToken = 'invalid_token';
      
      // Primeiro, invalida o refresh token armazenado
      secureLocalStorage.setItem('refreshToken', 'invalid_refresh_token');
      
      const response = await api.get('/api/Participants/info', {
        headers: {
          Authorization: `Bearer ${invalidToken}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async loginSSOAllianz(code, signal) {
    try {
      // SSO callback pode acontecer antes do tenant estar inicializado (ex.: host mapping / assets indisponíveis).
      // Não deve bloquear a tentativa de login, já que o endpoint recebe x-tenant-id explicitamente.
      try {
        await initializeTenant();
      } catch (tenantInitError) {
        console.warn('SSO Allianz: tenant initialization failed, proceeding with login:', tenantInitError);
      }

      // initializeTenant() não é abortável; verifica o sinal logo após para não fazer a chamada de rede
      // se o componente já foi desmontado enquanto o tenant inicializava
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      const response = await api.post('/api/participants/login-sso-allianz', {
        code: code
      }, {
        _skipLogoutOn401: true, // 401 no login = erro de credenciais; não disparar redirect de logout
        signal,
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-id': 'allianz'
        }
      });

      return processLoginResponse(response);
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('SSO Allianz login error:', error.response?.data || error.message);
      }
      throw error;
    }
  },

  async loginSSOSubway(code, signal) {
    try {
      // SSO callback pode acontecer antes do tenant estar inicializado (ex.: host mapping / assets indisponíveis).
      // Não deve bloquear a tentativa de login, já que o endpoint recebe x-tenant-id explicitamente.
      try {
        await initializeTenant();
      } catch (tenantInitError) {
        console.warn('SSO Subway: tenant initialization failed, proceeding with login:', tenantInitError);
      }

      // initializeTenant() não é abortável; verifica o sinal logo após para não fazer a chamada de rede
      // se o componente já foi desmontado enquanto o tenant inicializava
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      const response = await api.post('/api/participants/login-sso-subway', {
        code: code
      }, {
        _skipLogoutOn401: true,
        signal,
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-id': 'subway'
        }
      });

      return processLoginResponse(response);
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('SSO Subway login error:', error.response?.data || error.message);
      }
      throw error;
    }
  },

  async loginSSOTigre(code, signal) {
    try {
      // SSO callback pode acontecer antes do tenant estar inicializado (ex.: host mapping / assets indisponíveis).
      // Não deve bloquear a tentativa de login, já que o endpoint recebe x-tenant-id explicitamente.
      try {
        await initializeTenant();
      } catch (tenantInitError) {
        console.warn('SSO Tigre: tenant initialization failed, proceeding with login:', tenantInitError);
      }

      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      // Tigre usa o endpoint genérico login-by-auth-code com campo SSOCode
      const response = await api.post('/api/participants/login-by-auth-code', {
        SSOCode: code
      }, {
        _skipLogoutOn401: true,
        signal,
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-id': 'tigre'
        }
      });

      return processLoginResponse(response);
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('SSO Tigre login error:', error.response?.data || error.message);
      }
      throw error;
    }
  },

  async loginSSOAllianzInternal(code, signal) {
    try {
      // SSO callback pode acontecer antes do tenant estar inicializado (ex.: host mapping / assets indisponíveis).
      // Não deve bloquear a tentativa de login, já que o endpoint recebe x-tenant-id explicitamente.
      try {
        await initializeTenant();
      } catch (tenantInitError) {
        console.warn('SSO Allianz Internal: tenant initialization failed, proceeding with login:', tenantInitError);
      }

      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      // Allianz Internal usa o mesmo fluxo do Tigre: endpoint genérico login-by-auth-code com campo SSOCode
      const response = await api.post('/api/participants/login-by-auth-code', {
        SSOCode: code
      }, {
        _skipLogoutOn401: true,
        signal,
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-id': 'allianz'
        }
      });

      return processLoginResponse(response);
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('SSO Allianz Internal login error:', error.response?.data || error.message);
      }
      throw error;
    }
  },

  /**
   * Busca todos os segmentos ativos do participante pelo username.
   * Retorna array completo (vários segmentos simultâneos são suportados).
   * Ordena por createdAt descendente (mais recente primeiro) — prioridade para tema/logo.
   * @param {boolean} forceRefresh - Se true, ignora cache
   * @returns {Promise<Array>} Array de objetos {segmentId, ...campos}
   */
  async getUserSegments(forceRefresh = false) {
    try {
      await this.checkTokenExpiration();

      const authToken = secureLocalStorage.getItem('idToken');

      if (!authToken) {
        localStorage.removeItem('userSegments');
        return [];
      }

      if (!forceRefresh) {
        try {
          const cached = localStorage.getItem('userSegments');
          if (cached) {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed)) {
              const normalized = parsed.map(normalizeUserSegment).filter(Boolean);
              if (normalized.length > 0) {
                return normalized;
              }
            }
          }
        } catch (parseError) {
          console.warn('Error parsing cached userSegments, fetching new:', parseError);
          localStorage.removeItem('userSegments');
        }
      }

      let userName = secureLocalStorage.getItem('username');
      if (!userName) {
        try {
          const decodedToken = jwtDecode(authToken);
          userName = decodedToken['cognito:username'] || decodedToken.username || decodedToken.sub;
        } catch (decodeError) {
          console.error('Error decoding token for username:', decodeError);
        }
      }

      if (!userName) {
        localStorage.removeItem('userSegments');
        return [];
      }

      const response = await apiEngagement.get('/api/segmentParticipant/byParticipantUserName', {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { userName },
      });

      let rawList = [];
      if (Array.isArray(response.data) && response.data.length > 0) {
        rawList = response.data;
      } else if (normalizeUserSegment(response.data)) {
        rawList = [response.data];
      }

      // Filtra apenas os ativos; se nenhum vier ativo, usa a lista completa como fallback
      const activeList = rawList.filter(s => s.isActive === true);
      const candidates = activeList.length > 0 ? activeList : rawList;

      // Ordena por createdAt descendente (mais recente primeiro)
      const sorted = [...candidates].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });

      const segmentsData = sorted.map(normalizeUserSegment).filter(Boolean);

      try {
        localStorage.setItem('userSegments', JSON.stringify(segmentsData));
      } catch (storageError) {
        console.error('Error saving userSegments to localStorage:', storageError);
      }

      return segmentsData;
    } catch (error) {
      console.error('Get user segments error:', error.response?.data || error.message);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('userSegments');
      }
      return [];
    }
  },

  /**
   * Retorna os segmentIds do usuário do cache (síncrono), mais recente primeiro.
   * @returns {string[]}
   */
  getCurrentSegmentIdsSync() {
    if (this.isAuthenticated()) {
      try {
        const cached = localStorage.getItem('userSegments');
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed)) {
            return parsed
              .map(normalizeUserSegment)
              .filter(Boolean)
              .map(segment => segment.segmentId);
          }
        }
      } catch (error) {
        console.warn('Error reading userSegments from cache:', error);
      }
    }
    return [];
  },

  /**
   * Obtém o segmentId padrão (SEGMENT_DEFAULT) do tenant config (síncrono).
   * @returns {string|null}
   */
  getDefaultSegmentIdSync() {
    try {
      const tenantConfig = getTenantConfig();
      return (
        tenantConfig?.SEGMENT_DEFAULT ||
        tenantConfig?.FEATURE_FLAGS?.SEGMENT_DEFAULT ||
        null
      );
    } catch (error) {
      console.warn('Error getting SEGMENT_DEFAULT from tenant config:', error);
      return null;
    }
  },
};
